/**
 * Scene wind at runtime: the wind itself (settings, keyframes, clock), the wind-reactive splat
 * objects and areas it moves, and what the rest of the viewer reads from it (clouds that follow
 * it, weather and particles that drift, shadows that must redraw while splats move).
 *
 * - Areas (part of the scan, the shared region shape picker) are one render-stage effect in the
 *   splat region composer (center hook): see windShaders.ts. Only the clock and the area values
 *   change per frame, as material uniforms; nothing recompiles and no splat is re-copied.
 * - Objects (a separated or extra splat) carry a work-buffer modifier part; while the object is on
 *   screen the clock is pushed to it every frame, which re-copies that object's splats only.
 *
 * Nothing runs for a scene without wind settings or wind-reactive parts: no listeners, no shader
 * code, no frames. With parts, frames are requested only while the wind moves something on screen.
 */
import * as pc from 'playcanvas';
import type { SceneWindSettings, WindArea, WindResponse } from '../../types';
import { drawableAreas, areaBounds } from '../area-shape';
import { regionMatrix, writeRegionUniform, createRegionUniforms, type RegionUniform } from '../region-shape';
import type { RegionColorEffect } from '../splat-region-effects';
import { setWorkBufferModifierPart } from '../workBufferModifiers';
import { boxInFrustum } from '../clouds/cloudSystem';
import { holdParameterOnlySorts } from './gsplatSortHold';
import {
    MAX_WIND_AREAS,
    WIND_TIME_PERIOD,
    resolveSceneWind,
    resolveWindResponse,
    windDirectionRuntime,
    windGustLength,
    windPhaseSeed,
    windStrength,
    writeWindGlobalUniforms,
    writeWindPartUniforms,
    type ResolvedSceneWind,
    type ResolvedWindResponse,
    type WindPartExtent,
} from './windParams';
import {
    WIND_OBJECT_MODIFIER_GLSL,
    WIND_OBJECT_MODIFIER_WGSL,
    WIND_PART_UNIFORMS,
    WIND_UNIFORM_A,
    WIND_UNIFORM_B,
    windAreaCenterShader,
    windAreaUniform,
    isWindAreaShape,
    type WindAreaShape,
} from './windShaders';

export const WIND_EFFECT_KEY = 'wind';
type AnimatedWindField = 'direction' | 'speed' | 'gustiness' | 'turbulence';
const MODIFIER_KEY = 'wind';
/** Centre samples per object for its base, height and extent along the wind. */
const OBJECT_SAMPLES = 6000;

export interface WindSystemDeps {
    app: pc.AppBase;
    camera: pc.Entity;
    /** Scene-content root: areas are in its coordinates, and so is the wind. */
    sceneContent: pc.Entity;
    /** The splat region composer (splat-region-effects.ts). */
    regionEffects: { set(key: string, effect: RegionColorEffect | undefined): void };
    requestFrame: (reason: string) => void;
}

interface PartUniforms {
    a: Float32Array;
    b: Float32Array;
    c: Float32Array;
    d: Float32Array;
}
const newPartUniforms = (): PartUniforms => ({ a: new Float32Array(4), b: new Float32Array(4), c: new Float32Array(4), d: new Float32Array(4) });

interface AreaRuntime {
    id: string;
    area: WindAreaShape;
    response: ResolvedWindResponse;
    uniforms: PartUniforms;
    region: RegionUniform;
    /** Scene-space corner samples (direction-independent); extent along the wind from them. */
    corners: Float32Array;
    shape: WindExtentShape;
    /** The direction its uniforms were written for (NaN: write them). */
    extentDir: number;
    worldBox: pc.BoundingBox;
    onScreen: boolean;
}

interface ObjectRuntime {
    id: string;
    entity: pc.Entity;
    raw: WindResponse;
    response: ResolvedWindResponse;
    uniforms: PartUniforms;
    toScene: Float32Array;
    toWorld: Float32Array;
    phase: number;
    /** The component the modifier part is installed on (re-installed when the component changes). */
    installedOn: pc.GSplatComponent | null;
    /** Scene-space centre samples, what they were taken from, and their direction-independent extent. */
    samples: Float32Array | null;
    sampleKey: string;
    shape: WindExtentShape | null;
    extent: WindPartExtent | null;
    extentDir: number;
    worldBox: pc.BoundingBox;
    onScreen: boolean;
    /** Values last pushed to the component (each push re-copies the object's splats). */
    pushed: Map<string, Float32Array>;
}

type GsplatLike = pc.GSplatComponent & {
    resource?: { centers?: Float32Array | null; centersVersion?: number; aabb?: pc.BoundingBox | null; numSplats?: number } | null;
    unified?: boolean;
    setWorkBufferModifier?: unknown;
};

const _m = new pc.Mat4();
const _m2 = new pc.Mat4();
const _v = new pc.Vec3();
const _corners = Array.from({ length: 8 }, () => new pc.Vec3());

/** Where the q-quantile of n values sits once they are sorted. */
const quantileIndex = (n: number, q: number) => Math.min(n - 1, Math.max(0, Math.round((n - 1) * q)));

/** The q-quantile of values already sorted (0 for none). */
function sortedQuantile(sorted: Float32Array, q: number): number {
    return sorted.length ? sorted[quantileIndex(sorted.length, q)] : 0;
}

/**
 * The value a sort would put at index k of a[l..r] (NaN-free), in linear time: Wirth's
 * selection, which moves the values around so that a[l..k] are the smallest.
 */
function selectKth(a: Float32Array, k: number, l: number, r: number): number {
    while (l < r) {
        const x = a[k];
        let i = l, j = r;
        do {
            while (a[i] < x) i++;
            while (x < a[j]) j--;
            if (i <= j) {
                const t = a[i];
                a[i] = a[j];
                a[j] = t;
                i++;
                j--;
            }
        } while (i <= j);
        if (j < k) l = i;
        if (k < i) r = j;
    }
    return a[k];
}

function quantile(values: Float32Array, q: number): number {
    return sortedQuantile(Float32Array.from(values).sort(), q);
}

/** Scratch for the samples' projections on the wind (grown on demand, reused every call). */
let _along = new Float32Array(0);

export class WindSystem {
    private readonly deps: WindSystemDeps;
    private settings: SceneWindSettings | undefined;
    private base: ResolvedSceneWind = resolveSceneWind(undefined);
    private animated: Partial<Record<AnimatedWindField, number>> = {};
    private wind: ResolvedSceneWind = this.base;
    private time = 0;
    private clock: number | null = null;
    private readonly globals = { a: new Float32Array(4), b: new Float32Array(4) };
    private areas: AreaRuntime[] = [];
    private areaKey = '';
    private areaEffect: RegionColorEffect | undefined;
    private readonly objects = new Map<string, ObjectRuntime>();
    private listening = false;
    private animatingOnScreen = false;
    /** Editor: Splat Edit is open (areas move the scan, which it hides while it edits). */
    private areasPaused = false;
    private boundsId: string | null = null;
    private motion = 0;
    /**
     * The placements of the objects whose clock was pushed this frame: their re-copies only move
     * splats in the work buffer, so they need no CPU re-sort (gsplatSortHold.ts).
     */
    private readonly swayingPlacements = new Set<object>();
    private readonly heldPlacements = () => (this.swayingPlacements.size ? this.swayingPlacements : null);
    private destroyed = false;
    private readonly onUpdate = (dt: number) => this.tick(dt);

    constructor(deps: WindSystemDeps) {
        this.deps = deps;
    }

    // ── settings ──────────────────────────────────────────────────────────────────────────

    /** The scene's wind settings (scene field `wind`), applied in place. */
    setSettings(settings: SceneWindSettings | null | undefined): void {
        this.settings = settings && typeof settings === 'object' ? settings : undefined;
        this.base = resolveSceneWind(this.settings);
        this.refreshWind();
        this.rebuildAreas();
        this.syncListening();
        this.deps.requestFrame('wind');
    }

    /** The wind now (settings with keyframed values on top). */
    current(): ResolvedSceneWind {
        return this.wind;
    }

    /** Mark a splat object wind-reactive (a response that isn't disabled), or not (null). */
    setObject(id: string, entity: pc.Entity | null, response: WindResponse | null | undefined): void {
        const existing = this.objects.get(id);
        const on = !!entity && !!response && typeof response === 'object' && response.enabled !== false;
        if (!on) {
            if (existing) this.removeObject(id);
            return;
        }
        const resolved = resolveWindResponse(response);
        if (existing && existing.entity === entity) {
            existing.raw = { ...response };
            existing.response = resolved;
            existing.extentDir = NaN; // recompute the uniforms
        } else {
            if (existing) this.removeObject(id);
            this.objects.set(id, {
                id,
                entity: entity!,
                raw: { ...response },
                response: resolved,
                uniforms: newPartUniforms(),
                toScene: new Float32Array(16),
                toWorld: new Float32Array(16),
                phase: windPhaseSeed(id),
                installedOn: null,
                samples: null,
                sampleKey: '',
                shape: null,
                extent: null,
                extentDir: NaN,
                worldBox: new pc.BoundingBox(),
                onScreen: false,
                pushed: new Map(),
            });
        }
        this.syncListening();
        this.deps.requestFrame('wind');
    }

    removeObject(id: string): void {
        const obj = this.objects.get(id);
        if (!obj) return;
        this.objects.delete(id);
        if (obj.installedOn) {
            try {
                setWorkBufferModifierPart(obj.installedOn, MODIFIER_KEY, null);
            } catch {
                /* component destroyed with its entity */
            }
        }
        this.syncListening();
        this.deps.requestFrame('wind');
    }

    hasObject(id: string): boolean {
        return this.objects.has(id);
    }

    /** True while the wind moves a part that is on screen (on-demand rendering keeps drawing). */
    isAnimatingOnScreen(): boolean {
        return this.animatingOnScreen;
    }

    /** Changes on every frame the wind moves splats on screen (shadow maps redraw from it). */
    get motionVersion(): number {
        return this.motion;
    }

    /** Number of wind-reactive parts (objects and drawn areas). */
    get partCount(): number {
        return this.objects.size + this.areas.length;
    }

    /** Diagnostics / tests: hold the wind clock at `seconds` (null: run). */
    setClock(seconds: number | null): void {
        this.clock = typeof seconds === 'number' && Number.isFinite(seconds) ? seconds : null;
        this.deps.requestFrame('wind');
    }

    /**
     * Editor: while Splat Edit is open (SPLAT_EDIT_ACTIVE_EVENT) the scan the areas move is
     * hidden, so areas don't count as moving on screen and don't keep the editor redrawing.
     */
    setAreasPaused(paused: boolean): void {
        if (this.areasPaused === paused) return;
        this.areasPaused = paused;
        this.deps.requestFrame('wind');
    }

    /** Editor: outline one area's shape in the viewport (or none). */
    setAreaBoundsVisible(id: string | null): void {
        if (this.boundsId === id) return;
        this.boundsId = id;
        this.areaKey = '';
        this.rebuildAreas();
        this.deps.requestFrame('wind-bounds');
    }

    // ── keyframes (wind.direction / wind.speed / wind.gustiness / wind.turbulence) ─────────

    readAnimatable(): Record<string, number> {
        return {
            'wind.direction': this.wind.direction,
            'wind.speed': this.wind.speed,
            'wind.gustiness': this.wind.gustiness,
            'wind.turbulence': this.wind.turbulence,
        };
    }

    writeAnimatable(values: Map<string, number>): boolean {
        let touched = false;
        const scalar = (key: string, field: AnimatedWindField, lo: number, hi: number) => {
            if (!values.has(key)) return;
            const v = Math.min(hi, Math.max(lo, values.get(key)!));
            if (this.animated[field] !== v) {
                this.animated[field] = v;
                touched = true;
            }
        };
        scalar('wind.direction', 'direction', -1e6, 1e6);
        scalar('wind.speed', 'speed', 0, 1000);
        scalar('wind.gustiness', 'gustiness', 0, 1);
        scalar('wind.turbulence', 'turbulence', 0, 1);
        if (touched) {
            this.refreshWind();
            this.deps.requestFrame('wind');
        }
        return touched;
    }

    resetAnimatable(properties: string[]): void {
        for (const p of properties) {
            const field = p.replace(/^wind\./, '') as AnimatedWindField;
            delete this.animated[field];
        }
        this.refreshWind();
        this.deps.requestFrame('wind');
    }

    /** Editor: drop keyframed values so the configured wind shows. */
    clearAnimated(): void {
        this.animated = {};
        this.refreshWind();
    }

    debugInfo(): Record<string, unknown> {
        return {
            wind: { ...this.wind },
            strength: this.wind.enabled ? windStrength(this.wind.speed) : 0,
            time: this.time,
            clock: this.clock,
            animatingOnScreen: this.animatingOnScreen,
            listening: this.listening,
            motionVersion: this.motion,
            gustLength: this.globals.b[3],
            areas: this.areas.map((a) => ({ id: a.id, kind: a.area.kind, profile: a.response.profile, onScreen: a.onScreen, base: a.uniforms.a[0], height: a.uniforms.a[1] })),
            objects: [...this.objects.values()].map((o) => ({
                id: o.id,
                profile: o.response.profile,
                installed: !!o.installedOn,
                onScreen: o.onScreen,
                extent: o.extent ? { ...o.extent } : null,
            })),
        };
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        for (const id of [...this.objects.keys()]) this.removeObject(id);
        if (this.areaEffect) this.deps.regionEffects.set(WIND_EFFECT_KEY, undefined);
        this.areaEffect = undefined;
        if (this.listening) this.deps.app.off('update', this.onUpdate);
        this.listening = false;
    }

    // ── internals ─────────────────────────────────────────────────────────────────────────

    private refreshWind(): void {
        const a = this.animated;
        this.wind = {
            enabled: this.base.enabled,
            direction: a.direction ?? this.base.direction,
            speed: a.speed ?? this.base.speed,
            gustiness: a.gustiness ?? this.base.gustiness,
            turbulence: a.turbulence ?? this.base.turbulence,
        };
    }

    /** Listen to frames only while there is something the wind can move. */
    private syncListening(): void {
        const want = !this.destroyed && (this.objects.size > 0 || this.areas.length > 0);
        if (want && !this.listening) {
            this.deps.app.on('update', this.onUpdate);
            this.listening = true;
        } else if (!want && this.listening) {
            this.deps.app.off('update', this.onUpdate);
            this.listening = false;
            this.animatingOnScreen = false;
        }
    }

    private rebuildAreas(): void {
        const list = this.base.enabled && Array.isArray(this.settings?.areas) ? (this.settings!.areas as WindArea[]) : [];
        const drawable = drawableAreas<WindArea>(list.filter((a) => a && typeof a === 'object').map((a) => ({ ...a, apply: 'inside' as const })), MAX_WIND_AREAS)
            .filter(({ item }) => resolveWindResponse(item).enabled)
            // sphere / box / outline only: other shared shape kinds don't sway
            .flatMap(({ item, area }) => (isWindAreaShape(area) ? [{ item, area }] : []));
        // Structure (shape kinds, outlines) decides the shader; everything else rides in uniforms.
        const key = JSON.stringify(drawable.map(({ area }) => (area.kind === 'outline' ? { o: area.points, d: area.slab.depth, f: area.slab.feather } : area.kind)));
        const regions = createRegionUniforms(drawable.length);
        const previous = new Map(this.areas.map((a) => [a.id, a]));
        this.areas = drawable.map(({ item, area }, i) => {
            const old = previous.get(item.id);
            const corners = areaCorners(area);
            return {
                id: item.id,
                area,
                response: resolveWindResponse(item),
                uniforms: old?.uniforms ?? newPartUniforms(),
                region: old?.region ?? regions[i],
                corners,
                shape: extentShapeFromSamples(corners, false),
                extentDir: NaN,
                worldBox: new pc.BoundingBox(),
                onScreen: false,
            };
        });
        this.areas.forEach((a) => {
            if (a.area.kind !== 'outline') writeRegionUniform(a.region, a.area.region);
        });
        if (!this.areas.length) {
            if (this.areaEffect) this.deps.regionEffects.set(WIND_EFFECT_KEY, undefined);
            this.areaEffect = undefined;
            this.areaKey = '';
            this.syncListening();
            return;
        }
        this.writeUniforms();
        const fullKey = `${key}|${this.boundsId ?? ''}`;
        if (fullKey !== this.areaKey || !this.areaEffect) {
            this.areaKey = fullKey;
            const renderUniforms: Record<string, Float32Array> = {
                [WIND_UNIFORM_A]: this.globals.a,
                [WIND_UNIFORM_B]: this.globals.b,
            };
            this.areas.forEach((a, i) => {
                const name = windAreaUniform(i);
                renderUniforms[`${name}A`] = a.uniforms.a;
                renderUniforms[`${name}B`] = a.uniforms.b;
                renderUniforms[`${name}C`] = a.uniforms.c;
                renderUniforms[`${name}D`] = a.uniforms.d;
                if (a.area.kind !== 'outline') {
                    renderUniforms[name] = a.region.matrix;
                    renderUniforms[`${name}P`] = a.region.params;
                }
            });
            const resolved = this.areas.map((a) => a.area);
            this.areaEffect = {
                key: WIND_EFFECT_KEY,
                centerGlsl: windAreaCenterShader(resolved, 'glsl'),
                centerWgsl: windAreaCenterShader(resolved, 'wgsl'),
                bounds: this.areas.filter((a) => a.id === this.boundsId).map((a) => areaBounds(a.area)),
                renderUniforms,
            };
            this.deps.regionEffects.set(WIND_EFFECT_KEY, this.areaEffect);
        }
        this.syncListening();
    }

    /** The globals and every part's uniforms for the current wind and clock. */
    private writeUniforms(): void {
        let tallest = 0;
        for (const a of this.areas) tallest = Math.max(tallest, areaHeight(a.corners));
        for (const o of this.objects.values()) if (o.extent) tallest = Math.max(tallest, o.extent.height);
        writeWindGlobalUniforms(this.globals, this.wind, this.clock ?? this.time, windGustLength(this.wind.speed, tallest || 1));
        const dir = windDirectionRuntime(this.wind.direction);
        const dirKey = dir.x * 1000 + dir.z;
        for (const a of this.areas) {
            if (dirKey === a.extentDir) continue;
            a.extentDir = dirKey;
            writeWindPartUniforms(a.uniforms, a.response, extentAlongWind(a.corners, dir, false, a.shape), windPhaseSeed(a.id));
        }
    }

    private tick(dt: number): void {
        if (this.destroyed) return;
        const step = Math.min(Math.max(dt, 0), 0.25);
        this.time = (this.time + step) % WIND_TIME_PERIOD;
        this.writeUniforms();
        const strength = this.globals.a[1];
        const cam = this.deps.camera.camera;
        const frustum = cam?.frustum;
        const sceneWorld = this.deps.sceneContent.getWorldTransform();
        let onScreen = false;
        for (const a of this.areas) {
            a.worldBox.setFromTransformedAabb(boxFromSamples(a.corners), sceneWorld);
            a.onScreen = !this.areasPaused && !!frustum && boxInFrustum(a.worldBox, frustum);
            if (a.onScreen) onScreen = true;
        }
        const dir = windDirectionRuntime(this.wind.direction);
        this.swayingPlacements.clear();
        for (const obj of this.objects.values()) {
            if (this.syncObject(obj, sceneWorld, dir, !!frustum && !!cam, frustum ?? null, strength)) onScreen = true;
        }
        // WebGL2: an object's per-frame re-copy must not re-sort the whole scene (gsplatSortHold.ts).
        if (this.objects.size) holdParameterOnlySorts(this.deps.app, this.heldPlacements);
        const moving = strength > 0 && onScreen;
        this.animatingOnScreen = moving;
        if (moving) this.motion++;
    }

    /** Install, frame and push one object; true when it moves on screen this frame. */
    private syncObject(obj: ObjectRuntime, sceneWorld: pc.Mat4, dir: { x: number; z: number }, haveCamera: boolean, frustum: pc.Frustum | null, strength: number): boolean {
        const gs = obj.entity.gsplat as GsplatLike | undefined;
        if (!gs || !gs.unified) {
            obj.onScreen = false;
            return false;
        }
        if (obj.installedOn !== gs) {
            if (!setWorkBufferModifierPart(gs, MODIFIER_KEY, { glsl: WIND_OBJECT_MODIFIER_GLSL, wgsl: WIND_OBJECT_MODIFIER_WGSL })) return false;
            obj.installedOn = gs;
            obj.pushed.clear();
            obj.sampleKey = '';
        }
        const res = gs.resource;
        const world = obj.entity.getWorldTransform();
        // Samples (scene space) follow the object's transform and its splats (edits, streaming).
        const key = `${Array.from(world.data).join(',')}|${Array.from(sceneWorld.data).join(',')}|${res?.centersVersion ?? 0}|${res?.centers?.length ?? 0}`;
        if (key !== obj.sampleKey) {
            obj.sampleKey = key;
            obj.samples = sampleObject(res ?? null, world, sceneWorld);
            obj.shape = obj.samples ? extentShapeFromSamples(obj.samples, true) : null;
            obj.extentDir = NaN;
            _m.copy(sceneWorld).invert();
            obj.toScene.set(_m.data);
            obj.toWorld.set(sceneWorld.data);
        }
        if (!obj.samples || !obj.shape) return false;
        // On screen: its bounds, grown by how far it can sway.
        if (res?.aabb) {
            obj.worldBox.setFromTransformedAabb(res.aabb, world);
            const grow = obj.shape.height * Math.min(1, obj.response.bend * 2);
            obj.worldBox.halfExtents.add(_v.set(grow, grow * 0.3, grow));
        }
        obj.onScreen = haveCamera && !!frustum && obj.entity.enabled && boxInFrustum(obj.worldBox, frustum);
        // The extent along the wind follows its direction. New samples or a new response apply at
        // once; a new direction (every frame while it is keyframed) only while the object is on
        // screen: off screen it stands still, and catches up when it is back.
        const dirKey = dir.x * 1000 + dir.z;
        if (Number.isNaN(obj.extentDir) || (dirKey !== obj.extentDir && obj.onScreen)) {
            obj.extentDir = dirKey;
            obj.extent = extentAlongWind(obj.samples, dir, true, obj.shape);
            writeWindPartUniforms(obj.uniforms, obj.response, obj.extent, obj.phase);
        }
        // Every pushed value re-copies this object's splats: the clock only while it moves on
        // screen, everything else on change.
        const live = obj.onScreen && strength > 0;
        if (live || obj.pushed.size === 0) {
            this.push(obj, gs, WIND_UNIFORM_A, this.globals.a);
            this.push(obj, gs, WIND_UNIFORM_B, this.globals.b);
            // the engine's placement for this component: its re-copy this frame is the sway only
            const placement = (gs as unknown as { _placement?: object | null })._placement;
            if (live && placement) this.swayingPlacements.add(placement);
        }
        this.push(obj, gs, WIND_PART_UNIFORMS.a, obj.uniforms.a);
        this.push(obj, gs, WIND_PART_UNIFORMS.b, obj.uniforms.b);
        this.push(obj, gs, WIND_PART_UNIFORMS.c, obj.uniforms.c);
        this.push(obj, gs, WIND_PART_UNIFORMS.d, obj.uniforms.d);
        this.push(obj, gs, WIND_PART_UNIFORMS.toScene, obj.toScene);
        this.push(obj, gs, WIND_PART_UNIFORMS.toWorld, obj.toWorld);
        return live;
    }

    private push(obj: ObjectRuntime, gs: pc.GSplatComponent, name: string, value: Float32Array): void {
        const prev = obj.pushed.get(name);
        if (prev && prev.length === value.length && prev.every((v, i) => v === value[i])) return;
        const copy = new Float32Array(value);
        obj.pushed.set(name, copy);
        gs.setParameter(name, copy);
    }
}

// ── geometry helpers ──────────────────────────────────────────────────────────────────────

/** Points (scene space, xyz triples) that bound an area: its box corners or its outline slab. */
function areaCorners(area: WindAreaShape): Float32Array {
    if (area.kind === 'outline') {
        const { fit, depth } = area.slab;
        const out = new Float32Array(area.points.length * 6);
        area.points.forEach((p, i) => {
            out.set([p.x + fit.normal.x * depth, p.y + fit.normal.y * depth, p.z + fit.normal.z * depth], i * 6);
            out.set([p.x - fit.normal.x * depth, p.y - fit.normal.y * depth, p.z - fit.normal.z * depth], i * 6 + 3);
        });
        // The base of an outline drawn on the ground is the ground (its points), not the slab's floor.
        const minPointY = Math.min(...area.points.map((p) => p.y));
        for (let i = 1; i < out.length; i += 3) out[i] = Math.max(out[i], minPointY);
        return out;
    }
    const m = regionMatrix(area.region);
    const out = new Float32Array(24);
    for (let i = 0; i < 8; i++) {
        m.transformPoint(_v.set(i & 1 ? 1 : -1, i & 2 ? 1 : -1, i & 4 ? 1 : -1), _v);
        out.set([_v.x, _v.y, _v.z], i * 3);
    }
    return out;
}

function areaHeight(points: Float32Array): number {
    let lo = Infinity, hi = -Infinity;
    for (let i = 1; i < points.length; i += 3) {
        lo = Math.min(lo, points[i]);
        hi = Math.max(hi, points[i]);
    }
    return Number.isFinite(hi - lo) ? hi - lo : 0;
}

function boxFromSamples(points: Float32Array): pc.BoundingBox {
    const min = new pc.Vec3(Infinity, Infinity, Infinity);
    const max = new pc.Vec3(-Infinity, -Infinity, -Infinity);
    for (let i = 0; i + 2 < points.length; i += 3) {
        min.x = Math.min(min.x, points[i]);
        min.y = Math.min(min.y, points[i + 1]);
        min.z = Math.min(min.z, points[i + 2]);
        max.x = Math.max(max.x, points[i]);
        max.y = Math.max(max.y, points[i + 1]);
        max.z = Math.max(max.z, points[i + 2]);
    }
    const box = new pc.BoundingBox();
    if (Number.isFinite(min.x)) box.setMinMax(min, max);
    return box;
}

/** The part of a WindPartExtent that doesn't depend on the wind direction. */
export type WindExtentShape = Omit<WindPartExtent, 'upwind' | 'along'>;

/**
 * A part's base, height, axis and radius from scene-space samples: the direction-independent
 * part of its extent, worked out once per sample set. Robust (`robust`: 0.5% / 99.5% quantiles)
 * for splat centres, so a few stray splats don't move the anchor.
 */
export function extentShapeFromSamples(points: Float32Array, robust: boolean): WindExtentShape {
    const n = Math.floor(points.length / 3);
    const ys = new Float32Array(n);
    for (let i = 0; i < n; i++) ys[i] = points[i * 3 + 1];
    const lo = robust ? 0.005 : 0;
    const hi = robust ? 0.995 : 1;
    const sortedYs = Float32Array.from(ys).sort();
    const baseY = sortedQuantile(sortedYs, lo);
    const topY = sortedQuantile(sortedYs, hi);
    const height = Math.max(0, topY - baseY);
    // The axis: splat centres: the middle of the lowest 15% (the trunk); a box or outline: its middle.
    let axisX = 0, axisZ = 0, radius = 0;
    if (robust) {
        const low: number[] = [], lowZ: number[] = [];
        for (let i = 0; i < n; i++) if (ys[i] <= baseY + height * 0.15) { low.push(points[i * 3]); lowZ.push(points[i * 3 + 2]); }
        axisX = low.length ? quantile(Float32Array.from(low), 0.5) : 0;
        axisZ = lowZ.length ? quantile(Float32Array.from(lowZ), 0.5) : 0;
        const radial: number[] = [];
        for (let i = 0; i < n; i++) if (ys[i] >= baseY + height * 0.4) radial.push(Math.hypot(points[i * 3] - axisX, points[i * 3 + 2] - axisZ));
        radius = radial.length ? quantile(Float32Array.from(radial), 0.9) : height * 0.4;
    } else {
        let x0 = Infinity, x1 = -Infinity, z0 = Infinity, z1 = -Infinity;
        for (let i = 0; i < n; i++) {
            x0 = Math.min(x0, points[i * 3]); x1 = Math.max(x1, points[i * 3]);
            z0 = Math.min(z0, points[i * 3 + 2]); z1 = Math.max(z1, points[i * 3 + 2]);
        }
        axisX = (x0 + x1) / 2;
        axisZ = (z0 + z1) / 2;
        radius = Math.max(x1 - x0, z1 - z0) / 2;
    }
    return { baseY, height, axisX, axisZ, radius: Math.max(radius, height * 0.05, 1e-3) };
}

/**
 * The extent for one wind direction from the samples and their shape (extentShapeFromSamples
 * with the same `robust`): only the upwind edge and the length along the wind depend on the
 * direction. One projection of the samples into a reused buffer and two linear-time selections,
 * picking exactly the values a sort would (NaN last, like Float32Array#sort).
 */
export function extentAlongWind(points: Float32Array, dir: { x: number; z: number }, robust: boolean, shape: WindExtentShape): WindPartExtent {
    const n = Math.floor(points.length / 3);
    if (_along.length < n) _along = new Float32Array(n);
    const al = _along;
    let m = 0;
    for (let i = 0; i < n; i++) {
        const v = points[i * 3] * dir.x + points[i * 3 + 2] * dir.z;
        if (v === v) al[m++] = v;
    }
    let upwind = 0, downwind = 0;
    if (n) {
        const lo = quantileIndex(n, robust ? 0.005 : 0);
        const hi = quantileIndex(n, robust ? 0.995 : 1);
        downwind = hi < m ? selectKth(al, hi, 0, m - 1) : NaN;
        // after selecting hi, al[0..hi] hold the hi + 1 smallest values
        upwind = lo < m ? selectKth(al, lo, 0, Math.min(hi, m - 1)) : NaN;
    }
    return { ...shape, upwind, along: Math.max(0, downwind - upwind) };
}

/**
 * A part's base, height and extent along the wind from scene-space samples. Robust (`robust`:
 * 0.5% / 99.5% quantiles) for splat centres, so a few stray splats don't move the anchor.
 * Parts keep the shape and call extentAlongWind when only the direction changes.
 */
export function extentFromSamples(points: Float32Array, dir: { x: number; z: number }, robust: boolean): WindPartExtent {
    return extentAlongWind(points, dir, robust, extentShapeFromSamples(points, robust));
}

/** Up to OBJECT_SAMPLES of an object's splat centres in scene space (or its box corners). */
function sampleObject(res: GsplatLike['resource'] | null, world: pc.Mat4, sceneWorld: pc.Mat4): Float32Array | null {
    _m2.copy(sceneWorld).invert().mul(world); // local -> scene
    const centers = res?.centers;
    if (centers && centers.length >= 3) {
        const count = Math.floor(centers.length / 3);
        const stride = Math.max(1, Math.floor(count / OBJECT_SAMPLES));
        const out = new Float32Array(Math.ceil(count / stride) * 3);
        let k = 0;
        for (let i = 0; i < count; i += stride) {
            _v.set(centers[i * 3], centers[i * 3 + 1], centers[i * 3 + 2]);
            _m2.transformPoint(_v, _v);
            out[k++] = _v.x;
            out[k++] = _v.y;
            out[k++] = _v.z;
        }
        return out.subarray(0, k);
    }
    const aabb = res?.aabb;
    if (!aabb) return null;
    const c = aabb.center, h = aabb.halfExtents;
    const out = new Float32Array(24);
    for (let i = 0; i < 8; i++) {
        _corners[i].set(c.x + (i & 1 ? h.x : -h.x), c.y + (i & 2 ? h.y : -h.y), c.z + (i & 4 ? h.z : -h.z));
        _m2.transformPoint(_corners[i], _v);
        out.set([_v.x, _v.y, _v.z], i * 3);
    }
    return out;
}
